/* ═══════════════════════════════════════════════════════════════
   CollapsibleSection — a section header (chevron + title + optional count)
   that shows/hides its body. Built on a real `<button>` (no
   @radix-ui/react-collapsible in package.json — this is the whole
   primitive, not a placeholder for one): native Enter/Space activation,
   `aria-expanded`/`aria-controls` wired to the body's `id`, and the
   chevron's rotation is skipped under `prefers-reduced-motion` via CSS
   (see `.sf-collapsible-chevron` in editor.css).

   Used by toolboxes that need progressive disclosure in a narrow dock
   (the Modules panel's "In this document" / "Module library" sections) —
   generic enough for any future one.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Icon } from './Icon';

export interface CollapsibleSectionProps {
  /** Unique id — becomes the trigger's `aria-controls` target. */
  id: string;
  title: string;
  /** Shown muted after the title, e.g. a row count. */
  count?: number;
  expanded: boolean;
  onToggle: () => void;
  /** Rendered in the header row, after the trigger (e.g. a "Show all"
   *  link) — never inside the `<button>` itself, so it keeps its own
   *  click target. */
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
  /** Applied to the section's outer wrapper — lets a flex-column host
   *  give the EXPANDED section `flex: 1; min-height: 0` while a collapsed
   *  sibling takes only its header's height. */
  style?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
}

export function CollapsibleSection({
  id,
  title,
  count,
  expanded,
  onToggle,
  headerExtra,
  children,
  style,
  bodyStyle,
}: CollapsibleSectionProps) {
  const contentId = `${id}-body`;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        gap: 'var(--space-2)',
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <button
          type="button"
          id={`${id}-trigger`}
          className="sf-collapsible-trigger"
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={onToggle}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            flex: 1,
            minWidth: 0,
            // Padding, not bigger icon/text — the hit area grows to the
            // standard tap target (`--dsg-hit`, 2rem) without any visual
            // bloat; content stays centered and the same size it was.
            padding: 'var(--space-1) 0',
            minHeight: 'var(--dsg-hit, 2rem)',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            font: 'inherit',
            color: 'var(--tm)',
          }}
        >
          <Icon
            icon={ChevronRight}
            size="sm"
            aria-hidden="true"
            className="sf-collapsible-chevron"
            style={{ transform: expanded ? 'rotate(90deg)' : 'none', flexShrink: 0 }}
          />
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </span>
          {count != null && (
            <span style={{ fontSize: 'var(--font-size-xs)', flexShrink: 0 }}>({count})</span>
          )}
        </button>
        {headerExtra}
      </div>
      {expanded && (
        <div
          id={contentId}
          role="region"
          aria-labelledby={`${id}-trigger`}
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, ...bodyStyle }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default CollapsibleSection;
